import React, { useCallback, useMemo, forwardRef } from 'react';
import ContentSection from '@/pages/Products/VpsOciDetailPage/components/ContentSection';
import useLocale from '@/hooks/useLocale';
import * as Styles from './Management.styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  IconChevron,
} from 'gatorcomponents';
import { locale } from '@/utils/locale';
import { formatTimestampDate } from '@/utils/Formatters/formatTimestampDate';
import { useParams } from 'react-router-dom';
import { productsActions } from '@/redux/modules';
import { getIconBySlug } from '@/pages/Products/VpsOciDetailPage/utils/getIconBySlug';
import { backupAnalytics } from '@/analytics';

import { ENABLE_VPS_OCI_MANAGEMENT_SO_APPS } from '@/config/GrowthBook/constants';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import {
  openUrl,
  Management as ManagementRedirects,
  snapshotRedirect,
  openDomainUrl,
} from '@/utils/navigation/redirects';

const Management = forwardRef((props, ref) => {
  const {
    onGoToTemplatesPage,
  } = props;
  const dispatch = useDispatch();
  const vpsOci = useSelector(state => state.vpsOci);
  const {
    management, addons: addonsList, ssh, vps,
  } = (vpsOci && vpsOci.data) || {};
  const { serverIp } = ssh || {};
  const { domain } = vps || {};
  const enableVpsOciManagementSoApps = useFeatureIsOn(ENABLE_VPS_OCI_MANAGEMENT_SO_APPS);

  const n8nProducts = useSelector(state => state.products);
  const { n8n: n8nState } = n8nProducts || {};
  const { id: productId } = useParams();

  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: managementLocale } = productDetailsLocale;

  const soAppsRedirect = useCallback(() => {
    onGoToTemplatesPage && onGoToTemplatesPage();
  }, [onGoToTemplatesPage]);

  const n8nRedirect = useCallback(() => {
    if (!n8nState || !productsActions || !productsActions.n8n) return;

    const openN8N = (ip) => {
      const url = `http://${ip}`;
      openUrl(url);
    };

    if (n8nState.data && n8nState.hostingId === productId) {
      openN8N(n8nState.data.ip);
    } else {
      dispatch(productsActions.n8n.request({ hostingId: productId, actionType: 'open_n8n', callback: openN8N }));
    }
  }, [n8nState, productId, dispatch]);


  const checkExistSlug = useCallback(slug => !slug || slug === '' || managementLocale.management.items.findIndex(itemLocale => itemLocale.slug === slug) === -1, [managementLocale.management.items]);

  const getNameItem = useCallback((slug) => {
    if (!slug) return null;
    const item = managementLocale.management.items.find(itemLocale => itemLocale.slug === slug);
    return item ? item.name : '';
  }, [managementLocale.management.items]);

  const getInfoItem = useCallback((item) => {
    if (!item) return null;

    const { slug, info } = item;
    if (!slug && !info) return null;

    if (slug === 'so-apps') {
      const { version } = item;
      const name = (managementLocale.store.details.operationalSystems[info] && managementLocale.store.details.operationalSystems[info].name) || item.info;
      return name ? `${name}${version ? ` ${version}` : ''}` : '';
    }

    if (slug === 'backup' || slug === 'snapshot') {
      const { date } = item;
      if (!date) return info;
      return `${managementLocale.management.items.find(i => i.slug === slug).info} ${formatTimestampDate(date)}`;
    }

    return info;
  }, [managementLocale.store.details.operationalSystems, managementLocale.management.items]);

  const slugHandlers = useMemo(() => ({
    cpanel: () => ManagementRedirects.cpanelRedirect(productId, locale),
    n8n: n8nRedirect,
    plesk: () => ManagementRedirects.pleskRedirect(serverIp),
    'wordpress-1-clique': () => openDomainUrl(domain),
    'wordpress-openlitespeed': () => openDomainUrl(domain),
    wordpress: () => openDomainUrl(domain),
    docker: () => ManagementRedirects.dockerRedirect(serverIp),
    moodle: () => ManagementRedirects.moodleRedirect(serverIp),
    'node-js': () => ManagementRedirects.nodejsRedirect(serverIp),
    woocommerce: () => ManagementRedirects.woocommerceRedirect(domain),
    pterodactyl: () => ManagementRedirects.pterodactylRedirect(serverIp),
    laravel: () => ManagementRedirects.laravelRedirect(serverIp),
    cyberpanel: () => ManagementRedirects.cyberPanelRedirect(serverIp),
    magento: () => ManagementRedirects.magentoRedirect(domain),
    supabase: () => ManagementRedirects.supabaseRedirect(serverIp),
    'kubernetes-k3s': () => ManagementRedirects.kubernetesRedirect(serverIp),
    easypanel: () => ManagementRedirects.easyPanelRedirect(serverIp),
    webmin: () => ManagementRedirects.webminRedirect(serverIp),
    virtualmin: () => ManagementRedirects.virtualminRedirect(serverIp),
    'so-apps': soAppsRedirect,
    backup: () => {
      backupAnalytics.backupButtonClick();
      ManagementRedirects.backupRedirect(productId, locale);
    },
    snapshot: () => snapshotRedirect(productId, locale),
    'ssh-keys': () => ManagementRedirects.sshKeysRedirect(productId, locale),
    openclaw: () => ManagementRedirects.openClawRedirect(serverIp),
    gatorclaw: () => ManagementRedirects.gatorClawRedirect(serverIp),
    'evolution-go': () => ManagementRedirects.evolutionGoRedirect(serverIp),
    'evolution-api': () => ManagementRedirects.evolutionApiRedirect(serverIp),
    'evolution-crm': () => ManagementRedirects.evolutionCrmRedirect(serverIp),
  }), [productId, domain, serverIp, soAppsRedirect, n8nRedirect]);

  const handleClick = useCallback((item) => {
    if (!item) return;

    const handler = slugHandlers[item.slug];
    if (handler) {
      handler();
    }
  }, [slugHandlers]);

  const hasHandler = useCallback((item) => {
    if (!item) return false;

    return !!slugHandlers[item.slug];
  }, [slugHandlers]);

  if ((!management || management.length === 0) && (!addonsList || addonsList.length === 0)) return null;

  let managementList = [
    ...(management || []),
    ...(addonsList || []),
  ];

  if (!enableVpsOciManagementSoApps) {
    managementList = managementList.filter(item => item.slug !== 'so-apps');
  }

  if (managementList.length === 0) return null;

  return (
    <ContentSection
      ref={ref}
      direction="column"
      align="flex-start"
      justify="flex-start"
      title={managementLocale.management.title.text}
      tooltip={managementLocale.management.title.tooltip}
      testId="vps-management-section"
    >
      <Styles.ListItems>
        {managementList
          .filter(item => !checkExistSlug(item.slug))
          .map((item) => {
            const iconItem = getIconBySlug(item.slug);
            const itemName = getNameItem(item.slug);
            const itemInfo = getInfoItem(item);
            const itemHasHandler = hasHandler(item);

            return (
              <Styles.Item
                key={item.slug}
                onClick={itemHasHandler ? () => handleClick(item) : undefined}
                $itemHasHandler={itemHasHandler}
                data-testid={`vps-management-item-${item.slug}`}
              >
                {iconItem && <Styles.ItemIcon src={iconItem} alt={item.slug} data-testid={`vpsoci-management-icon-${item.slug}`} />}
                <Styles.ItemContent>
                  {itemName && <Styles.ItemName data-testid="vps-management-name">{itemName}</Styles.ItemName>}
                  {itemInfo && <Styles.ItemInfo data-testid="vps-management-info">{itemInfo}</Styles.ItemInfo>}
                </Styles.ItemContent>
                {itemHasHandler && (
                  <Styles.ItemArrow>
                    <IconChevron color="primaryMedium" size="lg" direction="right" />
                  </Styles.ItemArrow>
                )}
              </Styles.Item>
            );
          })}
      </Styles.ListItems>
    </ContentSection>
  );
});

Management.displayName = 'Management';

export default Management;
